import type { ReactNode } from "react";
import type { ModalOverlayProps as ReactAriaModalOverlayProps } from "react-aria-components";
import { Dialog as ReactAriaDialog, Modal as ReactAriaModal } from "react-aria-components";

import { useI18n } from "../use-i18n";
import { cn } from "../utils";
import { NcButton, type NcButtonProps } from "./nc-button";
import { NcIconCross } from "./nc-icons";

export interface NcDrawerProps extends ReactAriaModalOverlayProps {
  children: ReactNode;
  id?: string;
  "aria-label"?: string;
}

/** Renders a full-height panel sliding in from the left edge of the viewport, using the
 * same focus-trap/dismiss behaviour as NcModal (React Aria's Modal + Dialog), but
 * positioned against the side of the screen instead of centred.
 * `.react-aria-ModalOverlay` centres its content by default (see theme.css) — `justify-self-start`
 * and `self-stretch` below override that per-item so this panel pins to the left edge and
 * fills the full height instead.
 */
const NcDrawer = ({
  children,
  isDismissable = true,
  onOpenChange,
  className,
  "aria-label": ariaLabel,
  ...props
}: NcDrawerProps) => (
  <ReactAriaModal
    data-nc="NcDrawer"
    className={cn(
      "bg-light text-dark relative flex h-full w-full self-stretch justify-self-start shadow-xl outline-none md:w-1/4",
      "data-entering:animate-drawer-slide-in-left data-exiting:animate-drawer-slide-out-left",
      className
    )}
    isDismissable={isDismissable}
    onOpenChange={onOpenChange}
    {...props}
  >
    <ReactAriaDialog aria-label={ariaLabel} className="flex h-full w-full flex-col outline-none!">
      {children}
    </ReactAriaDialog>
  </ReactAriaModal>
);

export type NcDrawerCloseButtonProps = Omit<NcButtonProps, "children">;

/** A close (X) button intended to be placed inside NcDrawer's children, wherever the
 * consumer wants it. Not rendered automatically, so a drawer can omit it entirely (e.g.
 * when it's only ever closed via its own content, the overlay, or Escape).
 */
const NcDrawerCloseButton = ({
  variant = "icon",
  "aria-label": ariaLabel,
  className,
  ...props
}: NcDrawerCloseButtonProps) => {
  const { t } = useI18n();
  return (
    <NcButton
      data-nc="NcDrawer.CloseButton"
      variant={variant}
      aria-label={ariaLabel ?? t("close")}
      className={cn("absolute top-1 right-1 z-10 p-2 text-current", className)}
      {...props}
    >
      <NcIconCross />
    </NcButton>
  );
};

NcDrawer.CloseButton = NcDrawerCloseButton;

export { NcDrawer };
